// GEO_EXEC R10 / R11 / R18 endpoints.
//
// Every route in these three groups 404s while its switch is off, and the 404 body says WHICH 404
// it is: the switch returns `{"error": "not found"}`, an unowned brand returns
// `{"error": "brand not found"}`. `featureIsOff` in `stores/geoExecFeatures.js` reads that
// difference, which is why a client who simply mistyped a brand id never hides a shipped feature.

import { api } from "./client";

const id = (value) => encodeURIComponent(String(value));

// ── R10 · entity check (your website vs your GEO site vs what AI says) ─────────────────────────

export function getEntityCheck(brandId, { signal } = {}) {
  return api(`/api/geo-exec/r10/brands/${id(brandId)}/entity-check`, { signal });
}

export function recheckEntityCheck(brandId, { signal } = {}) {
  return api(`/api/geo-exec/r10/brands/${id(brandId)}/entity-check/recheck`, { method: "POST", signal, json: {} });
}

export function fixEntityFact(brandId, key, value, { signal } = {}) {
  return api(`/api/geo-exec/r10/brands/${id(brandId)}/entity-check/fix`, { method: "POST", signal, json: { key, value } });
}

// ── R11 · fact patrol (the same five facts across five columns) ────────────────────────────────

export function getFactPatrol(brandId, { signal } = {}) {
  return api(`/api/geo-exec/r11/brands/${id(brandId)}/fact-patrol`, { signal });
}

export function recheckFactPatrol(brandId, { signal } = {}) {
  return api(`/api/geo-exec/r11/brands/${id(brandId)}/fact-patrol/recheck`, { method: "POST", signal, json: {} });
}

/** FE-5 · put ONE fact mismatch on the plan, on demand.
 *
 * The server already turns mismatches into plan rows, but only in a batch during a re-check and
 * only up to `R11_MAX_ISSUES` (`app/services/geo_exec/r11/tasks.py` — `fact_correction`, which
 * builds each row with `entry_for(issue, brand_name)`). There is no route yet for "this one, now",
 * which is what the client is looking at when they read the row. **This call is the shape that
 * route has to answer**; until it exists the button says so rather than pretending it worked.
 *
 * Expected: `{"row": {…the R18/plan row…}}`; `409 {"error": "already_planned"}` when the issue is
 * already on the plan and `409 {"error": "cap_reached"}` at `R11_MAX_ISSUES`. */
export function addIssueToPlan(brandId, issueId, { signal } = {}) {
  return api(`/api/geo-exec/r11/brands/${id(brandId)}/fact-patrol/issues/${id(issueId)}/plan`, {
    method: "POST",
    signal,
    json: {},
  });
}

// ── R18 · recommendations ──────────────────────────────────────────────────────────────────────

export function getNextAction(brandId, { signal } = {}) {
  return api(`/api/geo-exec/r18/brands/${id(brandId)}/next-action`, { signal });
}

export function getRecommendations(brandId, { status, signal } = {}) {
  const query = status ? `?status=${encodeURIComponent(status)}` : "";
  return api(`/api/geo-exec/r18/brands/${id(brandId)}/recommendations${query}`, { signal });
}

export function approveRecommendation(recId, { signal } = {}) {
  return api(`/api/geo-exec/r18/recommendations/${id(recId)}/approve`, { method: "POST", signal, json: {} });
}

export function startRecommendation(recId, { signal } = {}) {
  return api(`/api/geo-exec/r18/recommendations/${id(recId)}/start`, { method: "POST", signal, json: {} });
}

export function doneRecommendation(recId, { signal } = {}) {
  return api(`/api/geo-exec/r18/recommendations/${id(recId)}/done`, { method: "POST", signal, json: {} });
}

export function dismissRecommendation(recId, reason = "", { signal } = {}) {
  return api(`/api/geo-exec/r18/recommendations/${id(recId)}/dismiss`, { method: "POST", signal, json: { reason } });
}
